/* ==========================================================================
   THE UNFOLD ARC - INSTANT SHOPIFY AJAX PREDICTIVE SEARCH ENGINE
   ========================================================================== */

(function () {
  'use strict';

  class PredictiveSearch {
    constructor() {
      this.overlay = document.querySelector('.search-overlay');
      if (!this.overlay) return;

      this.input = this.overlay.querySelector('.search-input');
      this.clearBtn = this.overlay.querySelector('.search-input-clear');
      this.closeBtn = this.overlay.querySelector('.search-close-btn');
      this.resultsContainer = this.overlay.querySelector('.predictive-search-content');
      this.loadingEl = this.overlay.querySelector('.predictive-search-loading');
      this.form = this.overlay.querySelector('form');

      this.debounceTimer = null;
      this.currentQuery = '';

      this.init();
    }

    init() {
      if (!this.input) return;

      this.input.addEventListener('input', this.handleInput.bind(this));
      this.input.addEventListener('focus', () => {
        const q = this.input.value.trim();
        if (q.length >= 1) {
          this.performSearch(q);
        }
      });

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          this.input.value = '';
          this.clearBtn.style.display = 'none';
          this.clearResults();
          this.input.focus();
        });
      }

      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          const q = this.input.value.trim();
          if (!q) {
            e.preventDefault();
          }
        });
      }
    }

    handleInput() {
      const query = this.input.value.trim();

      if (this.clearBtn) {
        this.clearBtn.style.display = query.length > 0 ? 'flex' : 'none';
      }

      if (query === this.currentQuery) return;

      clearTimeout(this.debounceTimer);

      if (query.length < 1) {
        this.currentQuery = '';
        this.clearResults();
        return;
      }

      // Fast 120ms debounce
      this.debounceTimer = setTimeout(() => {
        this.performSearch(query);
      }, 120);
    }

    // Helper: Strip HTML tags completely so raw HTML tags never display as text
    stripHtml(str) {
      if (!str) return '';
      if (typeof str !== 'string') str = String(str);
      return str.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    }

    // Helper: Extract valid image URL from Shopify product object (handles string, object, CDN protocol)
    extractImageUrl(p) {
      let url = '';
      if (typeof p.image === 'string' && p.image.trim()) {
        url = p.image;
      } else if (p.image && typeof p.image.url === 'string' && p.image.url.trim()) {
        url = p.image.url;
      } else if (p.image && typeof p.image.src === 'string' && p.image.src.trim()) {
        url = p.image.src;
      } else if (typeof p.featured_image === 'string' && p.featured_image.trim()) {
        url = p.featured_image;
      } else if (p.featured_image && typeof p.featured_image.url === 'string' && p.featured_image.url.trim()) {
        url = p.featured_image.url;
      } else if (p.featured_image && typeof p.featured_image.src === 'string' && p.featured_image.src.trim()) {
        url = p.featured_image.src;
      } else if (Array.isArray(p.images) && p.images.length > 0) {
        const first = p.images[0];
        if (typeof first === 'string') url = first;
        else if (first && first.url) url = first.url;
        else if (first && first.src) url = first.src;
      }

      if (!url) {
        return window.ShopifySearchAssets?.fallbackImage || '';
      }

      if (url.startsWith('//')) {
        url = 'https:' + url;
      }

      return url;
    }

    // Helper: Safely format price for display without raw HTML
    formatPrice(priceVal, priceFormatted) {
      if (priceFormatted) {
        const clean = this.stripHtml(priceFormatted);
        if (clean) return clean;
      }

      if (!priceVal && priceVal !== 0) return '₹3,490';

      if (typeof priceVal === 'string') {
        const clean = this.stripHtml(priceVal);
        if (clean.includes('₹') || clean.toLowerCase().includes('rs') || clean.includes('$')) {
          return clean;
        }
        priceVal = parseFloat(clean);
      }

      if (typeof priceVal === 'number' && !isNaN(priceVal)) {
        let num = priceVal;
        // In Shopify JSON, price might be in paise/cents e.g. 349000
        if (num > 10000 && Number.isInteger(num)) {
          num = num / 100;
        }
        return '₹' + Math.round(num).toLocaleString('en-IN');
      }

      return '₹' + priceVal;
    }

    // Helper: Ensure URL always links directly to the specific product page (/products/handle)
    extractProductUrl(p, root) {
      if (p.handle) {
        return `${root}/products/${p.handle}`;
      }
      let url = p.url || '';
      if (url.includes('/products/')) {
        const handle = url.split('/products/')[1].split('?')[0].split('#')[0];
        return `${root}/products/${handle}`;
      }
      if (url.startsWith('/')) {
        return `${root}${url}`;
      }
      return url || '#';
    }

    async performSearch(query) {
      this.currentQuery = query;
      this.showLoading(true);

      let results = [];
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root)
        ? Shopify.routes.root.replace(/\/$/, '')
        : '';

      // 1. Primary: Fetch Shopify live Predictive Search API
      try {
        const fetchUrl = `${root}/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`;
        const res = await fetch(fetchUrl);
        if (res.ok) {
          const data = await res.json();
          const products = data.resources?.results?.products || data.products || [];

          if (Array.isArray(products) && products.length > 0) {
            results = products.map(p => ({
              id: p.id || p.handle,
              title: this.stripHtml(p.title),
              url: this.extractProductUrl(p, root),
              price: this.formatPrice(p.price, p.price_formatted),
              image: this.extractImageUrl(p),
              category: this.stripHtml(p.vendor || p.product_type || 'Organic Staple')
            }));
          }
        }
      } catch (err) {
        console.warn('Shopify predictive search API fetch note:', err);
      }

      // 2. Fallback catalog matching if API returned 0 results or offline/preview
      if (results.length === 0) {
        const catalog = window.ShopifySearchAssets?.catalog || [];
        const qLower = query.toLowerCase();
        const qWords = qLower.split(/\s+/).filter(w => w.length > 0);

        results = catalog.filter(item => {
          const titleLower = this.stripHtml(item.title).toLowerCase();
          const catLower = this.stripHtml(item.category).toLowerCase();
          const tagsStr = (item.tags || []).join(' ').toLowerCase();
          const combined = `${titleLower} ${catLower} ${tagsStr}`;

          return qWords.every(word => combined.includes(word));
        });
      }

      this.showLoading(false);
      this.renderResults(query, results);
    }

    showLoading(state) {
      if (this.loadingEl) {
        this.loadingEl.style.display = state ? 'flex' : 'none';
      }
    }

    clearResults() {
      if (this.resultsContainer) {
        this.resultsContainer.innerHTML = '';
      }
      this.showLoading(false);
    }

    renderResults(query, items) {
      if (!this.resultsContainer) return;

      const cleanQuery = this.escapeHtml(this.stripHtml(query));

      if (items.length === 0) {
        this.resultsContainer.innerHTML = `
          <div class="predictive-search-empty">
            <p>No products found for "<strong>${cleanQuery}</strong>"</p>
            <span>Try typing 'polo', 'crew', 'shirt', or 'knit'.</span>
          </div>
        `;
        return;
      }

      let html = `<div class="predictive-search-grid">`;
      items.forEach(item => {
        const cleanTitle = this.escapeHtml(this.stripHtml(item.title));
        const cleanCategory = this.escapeHtml(this.stripHtml(item.category));
        const cleanPrice = this.escapeHtml(this.stripHtml(item.price));

        html += `
          <a href="${item.url}" class="predictive-search-item">
            <div class="predictive-item-img-wrapper">
              ${item.image ? `<img src="${item.image}" alt="${cleanTitle}" class="predictive-item-img" loading="lazy">` : `<div class="predictive-item-img-placeholder"></div>`}
            </div>
            <div class="predictive-item-info">
              <span class="predictive-item-category">${cleanCategory}</span>
              <h4 class="predictive-item-title">${cleanTitle}</h4>
              <div class="predictive-item-price">${cleanPrice}</div>
            </div>
            <svg viewBox="0 0 24 24" width="16" height="16" class="predictive-arrow" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </a>
        `;
      });
      html += `</div>`;

      // View all results button
      html += `
        <div class="predictive-search-footer">
          <button type="submit" form="${this.form?.id || ''}" class="btn-predictive-all" onclick="this.closest('.search-overlay').querySelector('form').submit();">
            View all results for "${cleanQuery}" →
          </button>
        </div>
      `;

      this.resultsContainer.innerHTML = html;
    }

    escapeHtml(str) {
      if (!str) return '';
      return String(str).replace(/[&<>"']/g, function (m) {
        return {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#039;'
        }[m];
      });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    new PredictiveSearch();
  });
})();
